import { Link } from '@material-ui/core';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import React, { useCallback, VFC } from 'react';

import { makeStyles } from '~/@components/@theme';
import { showDeleteConfirmationDialog } from '~/@components/DeleteButton';
import { FormCard } from '~/@components/FormCard';
import {
    FormikNumberField,
    FormikSelectField,
    FormikTextField,
    FormikTypeUnsafeNumberField,
} from '~/@components/FormikFields';
import { Grid } from '~/@components/Grid';
import { LocalizableText } from '~/@components/LocalizableText';
import { Text } from '~/@components/Text';
import { useVehicleTypePermissions } from '~/@store/vehicleTypes';
import { useVehicleTypeCreate, useVehicleTypeRemove, useVehicleTypeUpdate, VehicleType } from '~/@store/vehicleTypes';
import { commonPrecisions } from '~/config/enum';
import { VehicleKind } from '~/graphql';
import i18n from '~/i18n';

import { getVehicleTypeFormikConfig } from './VehicleTypeFormik';

type VehicleTypeFormProps = {
    onClose: () => void;
    vehicleType?: VehicleType;
};

export const VehicleTypeForm: VFC<VehicleTypeFormProps> = ({ onClose, vehicleType = null }) => {
    const { formCard, container } = useStyles();

    const [createVehicleType, isCreating] = useVehicleTypeCreate();
    const [updateVehicleType, isUpdating] = useVehicleTypeUpdate(vehicleType?.id || '');
    const [deleteVehicleType, isDeleting] = useVehicleTypeRemove(vehicleType?.id || '');

    const onDeleteClick = useCallback(() => {
        if (!vehicleType) return;

        return showDeleteConfirmationDialog(
            <LocalizableText code={'VehicleType.deleteConfirmation'} />,
            vehicleType.name
        ).then(confirmed => {
            if (confirmed) {
                deleteVehicleType();
                onClose();
            }
        });
    }, [deleteVehicleType, vehicleType, onClose]);

    const formik = useFormik(
        getVehicleTypeFormikConfig(vehicleType, vehicleType ? updateVehicleType : createVehicleType, onClose)
    );

    const { canCreate, canUpdate, canDelete } = useVehicleTypePermissions(vehicleType);
    const canEdit = vehicleType ? canUpdate : canCreate;

    return (
        <FormCard
            className={formCard}
            title={<LocalizableText code={vehicleType ? 'VehicleType.editTitle' : 'VehicleType.createTitle'} />}
            onClose={onClose}
            onSubmit={formik.handleSubmit}
            onDelete={!!vehicleType ? onDeleteClick : undefined}
            isCloseDisabled={formik.isSubmitting}
            isSubmitDisabled={!formik.dirty || formik.isSubmitting || !canEdit}
            isDeleteDisabled={formik.isSubmitting || !canDelete}
            showProgress={isCreating || isUpdating || isDeleting}>
            <FormikProvider value={formik}>
                <div className={container}>
                    <FormikSelectField
                        label={<LocalizableText code={'VehicleType.kind'} />}
                        name="kind"
                        formik={formik}
                        items={[VehicleKind.ON_SITE, VehicleKind.TRANSPORT]}
                        renderValue={i => i}
                        keyGetter={i => i}
                        disabled={!!vehicleType}
                    />
                    <FormikTextField
                        label={<LocalizableText code={'VehicleType.name'} />}
                        name="name"
                        formik={formik}
                        disabled={!canEdit}
                    />
                    <FormikTextField
                        label={<LocalizableText code={'VehicleType.externalId'} />}
                        name="externalId"
                        formik={formik}
                        disabled={!canEdit}
                    />
                    <FormikNumberField
                        label={<LocalizableText code={'VehicleType.beastId'} />}
                        name="beastId"
                        formik={formik}
                        disabled={!canEdit}
                    />
                    <Link href="https://docs.peppol.eu/logistics/2024-Q1/codelist/UNECERec28/" target="_blank">
                        <LocalizableText code={'VehicleType.beastListLink'} />
                    </Link>
                    <Text variant="BodyBold" color="textSecondary">
                        <LocalizableText code="VehicleType.fuelSettings" />
                    </Text>
                    <FieldArray name="fuelSettings">
                        {() => (
                            <Grid container alignItems="center" spacing={2}>
                                {formik.values.fuelSettings.map((fs, index) => (
                                    <React.Fragment key={fs.fuel}>
                                        <Grid item xs={2}>
                                            {fs.fuel}
                                        </Grid>
                                        <Grid item xs={5}>
                                            <FormikTypeUnsafeNumberField
                                                name={`fuelSettings.${index}.consumption`}
                                                label={
                                                    <>
                                                        <LocalizableText code="VehicleType.consumption" /> (
                                                        {i18n.VehicleType.perKm})
                                                    </>
                                                }
                                                fullWidth
                                                precision={commonPrecisions.decimal}
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <FormikTypeUnsafeNumberField
                                                name={`fuelSettings.${index}.consumptionPerHour`}
                                                label={
                                                    <>
                                                        <LocalizableText code="VehicleType.consumption" /> (
                                                        {i18n.VehicleType.perHour})
                                                    </>
                                                }
                                                fullWidth
                                                precision={commonPrecisions.decimal}
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                        <Grid item xs={2} />
                                        <Grid item xs={5}>
                                            <FormikTypeUnsafeNumberField
                                                name={`fuelSettings.${index}.co2Emission`}
                                                label={<LocalizableText code="VehicleType.co2Emission" />}
                                                fullWidth
                                                precision={commonPrecisions.decimal}
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                        <Grid item xs={5} />
                                    </React.Fragment>
                                ))}
                            </Grid>
                        )}
                    </FieldArray>
                </div>
            </FormikProvider>
        </FormCard>
    );
};

const useStyles = makeStyles(theme => ({
    formCard: {
        minWidth: 394,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
}));
